import React, { useState, useEffect } from 'react';
import { Box, Typography, Accordion, Slider, Loader, Badge, Button } from '@lucid/lego';
import {
  useGetAlertDetails,
  useResolveAlert,
} from '../../../../api/services/lupp/fhm-alerts/fhm.alerts';
import { formatStatus, getPriorityBadgeColor, getStatusBadgeColor } from '../utils';
import WorkOrderSelection from './WorkOrderSelection';
import styles from './AlertDetails.module.scss';

interface AlertDetailsProps {
  theme: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
  alertId: string;
}

type ViewMode = 'details' | 'workOrderSelection';

const AlertDetails: React.FC<AlertDetailsProps> = ({ theme, isOpen, onClose, alertId }) => {
  const { data: alert, isLoading, error } = useGetAlertDetails({ alertId });
  const resolveMutation = useResolveAlert();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [viewMode, setViewMode] = useState<ViewMode>('details');

  // Reset viewMode when alertId changes or slider closes
  useEffect(() => {
    if (!isOpen || alertId) {
      setViewMode('details');
    }
  }, [alertId, isOpen]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const handleResolveClick = () => {
    setViewMode('workOrderSelection');
  };

  const handleBack = () => {
    setViewMode('details');
  };

  const handleWorkOrderSubmit = async (workOrderId: string) => {
    if (!alertId || !alert?.repairPartnerId) return;

    try {
      await resolveMutation.mutateAsync({
        alertId,
        data: {
          repairPartnerId: alert.repairPartnerId,
          workOrderIdentifier: workOrderId,
          comments: 'Alert resolved from Partner Portal',
        },
      });
      setViewMode('details');
      onClose();
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  const isResolved = alert?.status?.toUpperCase() === 'RESOLVED';

  const footer =
    alert && !isResolved && viewMode === 'details' ? (
      <Box className={styles.footer}>
        <Button
          label="Resolve Alert"
          size="medium"
          color="neutral"
          theme={invertedTheme}
          onClick={handleResolveClick}
          gaComponent="alert-details-resolve"
          dataTestId="resolve-alert-button"
        />
      </Box>
    ) : undefined;

  const renderContent = () => {
    if (viewMode === 'workOrderSelection') {
      return (
        <WorkOrderSelection
          theme={invertedTheme}
          onBack={handleBack}
          onSubmit={handleWorkOrderSubmit}
          isSubmitting={resolveMutation.isPending}
        />
      );
    }

    if (isLoading) {
      return (
        <Box pt={48} pb={48} className="fetchingOverlay">
          <Loader size={32} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box pt={48} pb={48}>
          <Typography variant="body" size="large" theme={invertedTheme} color="error-1">
            Error loading alert details. Please try again.
          </Typography>
        </Box>
      );
    }

    if (!alert) {
      return null;
    }

    return (
      <Box className={styles.detailsContainer}>
        {/* Alert Overview */}
        <Accordion
          title="Alert Overview"
          theme={invertedTheme}
          defaultOpen={true}
          showBorder={true}
        >
          <Box className={styles.detailsContainer}>
            <Box className={styles.innerBox}>
              <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                Alert ID
              </Typography>
              <Box>
                <Typography variant="body" size="small" theme={invertedTheme} color="white">
                  {alert.id}
                </Typography>
              </Box>
            </Box>
            <Box className={styles.innerBox}>
              <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                Subject
              </Typography>
              <Box>
                <Typography variant="body" size="small" theme={invertedTheme} color="white">
                  {alert.subject || '-'}
                </Typography>
              </Box>
            </Box>
            {alert.message && (
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Message
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {alert.message}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box className={styles.gridView}>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Status
                </Typography>
                <Box>
                  {alert.status && (
                    <Badge
                      label={formatStatus(alert.status)}
                      color={getStatusBadgeColor(alert.status)}
                      type="secondary"
                      theme={invertedTheme}
                    />
                  )}
                </Box>
              </Box>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Priority
                </Typography>
                <Box>
                  {alert.severity && (
                    <Badge
                      label={alert.severity}
                      color={getPriorityBadgeColor(alert.severity)}
                      type="secondary"
                      theme={invertedTheme}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Accordion>

        {/* Vehicle Information */}
        <Accordion
          title="Vehicle Information"
          theme={invertedTheme}
          defaultOpen={true}
          showBorder={true}
        >
          <Box className={styles.detailsContainer}>
            <Box className={styles.innerBox}>
              <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                VIN
              </Typography>
              <Box>
                <Typography variant="body" size="small" theme={invertedTheme} color="white">
                  {alert.vin || '-'}
                </Typography>
              </Box>
            </Box>
            {alert.repairPartnerId && (
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Repair Partner ID
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {alert.repairPartnerId}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Accordion>

        {/* Timeline */}
        <Accordion title="Timeline" theme={invertedTheme} defaultOpen={true} showBorder={true}>
          <Box className={styles.detailsContainer}>
            <Box className={styles.gridView}>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Alert Received Date
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {formatDate(alert.createdAt)?.split(',')[0] || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Alert Received Time
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {formatDate(alert.createdAt)?.split(',').slice(1).join(',').trim() || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {alert.updatedAt && (
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Last Updated
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {formatDate(alert.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Accordion>

        {/* Work Order Information */}
        {alert.workOrderIdentifier && (
          <Accordion
            title="Work Order Information"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.detailsContainer}>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Work Order Identifier
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {alert.workOrderIdentifier}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Accordion>
        )}
      </Box>
    );
  };

  return (
    <Slider
      isOpen={isOpen && !!alertId}
      onClose={onClose}
      title={
        viewMode === 'workOrderSelection' ? 'Resolve Alert' : alert?.subject || alert?.id || '-'
      }
      width="1000px"
      direction="right"
      theme={invertedTheme}
      footer={footer}
    >
      {renderContent()}
    </Slider>
  );
};

export default AlertDetails;
